import { Alert, Button, Modal, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from "react";
import { useFetcher, useLoaderData } from 'react-router-dom';
import { uploadFileFromObject } from "../../../api/common";
import { Box } from "../../../components/design";
import { InputWrapper } from "../../../components/inputs/input-wrappers.jsx";
import { useUpdateQueryStringValueWithoutNavigation } from "../../../hooks/use-update-query-string-without-navigation.js";
import { formatDeliveryAddress } from '../../../library/helpers';
import { formatDate } from '../../../library/utils/intl.js';

import MultifileUpload from "../../../ui/multifile-upload.jsx";
import styles from './intransit.module.scss';
import { getAdjustedDeliveryDays } from '../../../library/utils/deliverydate.js';
import MissingFinisherAddress from './missingFinisherAddress.jsx';
import InfoLabel, { DATE_HELP } from '../../../components/data/info-label.jsx';
import { showAlert } from '../../../components/message/alert.jsx';

const DeliveryConfirm = ({ item, onConfirm }) => {
    const address =
        <>{formatDeliveryAddress(item)}</>

    return (
        <div className={styles.deliveryRow}>
            <div className="quantity">{item.quantity}</div>
            <div className="address">{address}</div>
            <div className="field-quantity">{item.ship_quantity ?? item.quantity}</div>
            <div className="field-expected_date">
                {formatDate(item.dispatch_date)}
            </div>
            <div className="field-trackingurl"><div><a href={item.tracking_url} target="blank" rel="noreferrer noopener">View</a></div></div>
            <div className="field-expected_date">{formatDate(item.expected_date)}</div>
            {item.status === 4 ? (
                <Button
                    type="primary"
                    name="action"
                    disabled
                >Proof Sent</Button>
            ) : (
                <Button
                    type="primary"
                    name="action"
                    value="confirmDeliveryByOnlyVendor"
                    onClick={onConfirm}
                >Confirm</Button>
            )}

        </div>
    )
}
DeliveryConfirm.propTypes = {
    item: PropTypes.object.isRequired,
    onConfirm: PropTypes.func.isRequired,
}

const InTransit = () => {
    const { productionDetails } = useLoaderData()
    const fetcher = useFetcher();
    const { errors = {} } = fetcher.data || {}
    const fileUploaderRef = useRef([])
    const inputComment = useRef('')
    const [isUploading, setIsUploading] = useState(false)
    const submittedRef = useRef(false)
    const delivery = productionDetails.item_delivery_address?.[0]
    const expectedDeliveryDays = getAdjustedDeliveryDays(delivery?.expected_date)

    const [confirmDeliveryId, setConfirmDeliveryId] = useUpdateQueryStringValueWithoutNavigation('confirmdelivery')
    const isSubmitting = fetcher.state !== 'idle' && fetcher.json?.action === 'confirmDeliveryByOnlyVendor'
    const isBusy = isUploading || isSubmitting

    const resetConfirmDeliveryPopup = () => {
        setConfirmDeliveryId(null)
        inputComment.current = ''
        fileUploaderRef.current = []
    }

    // Close the popup only once the server accepted the proof; on an error it
    // stays open with the selected files so the user can retry.
    useEffect(() => {
        if (!submittedRef.current || fetcher.state !== 'idle') return
        submittedRef.current = false
        if (fetcher.data === true) resetConfirmDeliveryPopup()
    }, [fetcher.state, fetcher.data])

    const handleFilesUpload = async () => {
        if (fileUploaderRef.current.length === 0) {
            showAlert('Please select atleast one file')
            return
        }
        setIsUploading(true)
        try {
            const fileIds = await Promise.all(fileUploaderRef.current.map(file => uploadFileFromObject(file)))
            submittedRef.current = true
            fetcher.submit({
                image_ids: fileIds,
                comment: inputComment.current,
                action: 'confirmDeliveryByOnlyVendor'
            }, {
                method: 'post',
                encType: 'application/json'
            })
        }
        catch (error) {
            message.error(error.message || 'Unable to upload files, please try again')
        }
        finally {
            setIsUploading(false)
        }
    }

    const closeConfirmDeliveryPopup = () => {
        if (isBusy) return //prevent closing popup while file is being uploaded
        resetConfirmDeliveryPopup()
    };

    if (!delivery) return <MissingFinisherAddress />

    return (
        <div className={styles.container}>
            <h3>Task For You</h3>
            <Alert
                description={
                    expectedDeliveryDays === 0
                        ? `You have shipped the order, the delivery is expected today.`
                        : `You have shipped the order, the delivery will take ${expectedDeliveryDays} to ${expectedDeliveryDays + 1} days.`
                }
                type="info"
                showIcon
            />
            <Box header={
                <div className={styles.deliveryRowHead}>
                    <div>Quantity</div>
                    <div>Delivery Address</div>
                    <div>Ship Quantity</div>
                    <div><InfoLabel label="Expected Dispatch Date" info={DATE_HELP.supplierDispatch} /></div>
                    <div>Tracking URL</div>
                    <div><InfoLabel label="Expected Delivery Date" info={DATE_HELP.supplierDelivery} /></div>
                    <div></div>
                </div>
            }>
                <DeliveryConfirm
                    item={delivery}
                    onConfirm={() => {
                        setConfirmDeliveryId(delivery.id)
                    }}
                />
            </Box>
            <Modal
                title="Upload Proof of Delivery"
                open={confirmDeliveryId}
                onOk={() => handleFilesUpload()}
                confirmLoading={isBusy}
                okText="Continue"
                onCancel={closeConfirmDeliveryPopup}
            >
                <div className={styles.uploadBox}>
                    <p>The uploaded printscreen should not contain track and trace code, country of origin and sender information</p>
                    <div className="main">
                        <div className="box--white">
                            <MultifileUpload ref={fileUploaderRef} />
                        </div>
                    </div>
                    <br />
                    <InputWrapper name="comment" label="Comment" defaultValue="" textarea allowClear errors={errors} onChange={e => inputComment.current = e.target.value} />
                </div>
            </Modal>
        </div>
    )
}

export default InTransit;